import {
  ChangeEvent,
  memo,
  useRef,
  useEffect,
  forwardRef,
  RefObject,
  useCallback,
  useState,
  useImperativeHandle,
} from "react"

import { v4 as uuidv4 } from "uuid"

import {
  InvisibleSelector,
  ControlContainer,
  SelectorStateClassnames,
  DecorativeContainer,
  useFocusHandling,
} from "./base"
import { CheckboxStyles } from "./checkbox-styles"
import { Label } from "./label"
import { RadioStyles } from "./radio-styles"
import { SwitchStyles } from "./switch-styles"
import {
  CheckboxProps,
  SwitchProps,
  RadioButtonProps,
  RadioChangeEvent,
  SelectorElementProps,
  RadioButtonDecorativeProps,
  CheckboxDecorativeProps,
  CheckboxChangeEvent,
} from "./types"

export * from "./types"

export { SelectorStateClassnames }

const useLabelAndMessageId: (id?: string) => { labelId: string; messageId: string } = (
  id,
) => ({
  labelId: useRef(id ? `${id}_label` : uuidv4()).current,
  messageId: useRef(id ? `${id}_message` : uuidv4()).current,
})

const useHandleChange = (onChange?: CheckboxChangeEvent) =>
  useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked, e)
    },
    [onChange],
  )

const useRadioHandleChange = (onChange?: RadioChangeEvent) =>
  useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value, e)
    },
    [onChange],
  )

const useIndeterminate = (
  selectorRef: RefObject<HTMLInputElement>,
  checked: boolean,
  indeterminate?: boolean,
) =>
  useEffect(() => {
    if (!checked && selectorRef?.current != null) {
      selectorRef.current.indeterminate = Boolean(indeterminate)
    }
  }, [indeterminate, checked])

export const Switch = memo<SwitchProps>(
  forwardRef<HTMLInputElement, SwitchProps>(
    (
      {
        label,
        message,
        className,
        onBlur,
        hideLabel,
        onChange,
        marginBottom,
        marginTop,
        labelPosition = "left",
        ...props
      },
      ref,
    ) => {
      const [noFocus, setNoFocus] = useState(false)
      const selectorRef = useRef<HTMLInputElement>(null)
      const { handleClick, handleBlur } = useFocusHandling(
        selectorRef,
        setNoFocus,
        onBlur,
      )
      const { labelId, messageId } = useLabelAndMessageId(props.id)
      const handleChange = useHandleChange(onChange)
      const selectorClassName = noFocus ? "no-focus" : ""
      useImperativeHandle(ref, () => selectorRef.current as HTMLInputElement)

      return (
        <ControlContainer
          disabled={props.disabled}
          className={className}
          marginBottom={marginBottom}
          marginTop={marginTop}
          onClick={handleClick}
        >
          <InvisibleSelector
            {...props}
            type="checkbox"
            onChange={handleChange}
            onBlur={handleBlur}
            className={selectorClassName}
            ref={selectorRef}
            aria-labelledby={labelId}
            aria-describedby={message && messageId}
          />
          <SwitchStyles />
          <Label
            label={label}
            message={message}
            labelId={labelId}
            messageId={messageId}
            labelPosition={labelPosition}
            hideLabel={hideLabel}
          />
        </ControlContainer>
      )
    },
  ),
)

export const RadioButton = memo<RadioButtonProps>(
  forwardRef<HTMLInputElement, RadioButtonProps>(
    (
      {
        label,
        message,
        className,
        onBlur,
        error,
        hideLabel,
        onChange,
        marginBottom,
        marginTop,
        labelPosition = "right",
        ...props
      },
      ref,
    ) => {
      const [noFocus, setNoFocus] = useState(false)
      const selectorRef = useRef<HTMLInputElement>(null)
      const { handleClick, handleBlur } = useFocusHandling(
        selectorRef,
        setNoFocus,
        onBlur,
      )
      const { labelId, messageId } = useLabelAndMessageId(props.id)
      const handleChange = useRadioHandleChange(onChange)
      const selectorClassName = noFocus ? "no-focus" : ""
      useImperativeHandle(ref, () => selectorRef.current as HTMLInputElement)

      return (
        <ControlContainer
          disabled={props.disabled}
          className={className}
          marginBottom={marginBottom}
          marginTop={marginTop}
          onClick={handleClick}
        >
          <InvisibleSelector
            {...props}
            type="radio"
            onBlur={handleBlur}
            onChange={handleChange}
            className={selectorClassName}
            ref={selectorRef}
            aria-invalid={error}
            aria-labelledby={labelId}
            aria-describedby={message && messageId}
          />
          <RadioStyles />
          <Label
            label={label}
            message={message}
            labelId={labelId}
            messageId={messageId}
            labelPosition={labelPosition}
            hideLabel={hideLabel}
          />
        </ControlContainer>
      )
    },
  ),
)

export const Checkbox = memo<CheckboxProps>(
  forwardRef<HTMLInputElement, CheckboxProps>(
    (
      {
        label,
        message,
        className,
        onBlur,
        indeterminate,
        error,
        hideLabel,
        onChange,
        marginBottom,
        marginTop,
        disabled,
        loading,
        labelPosition = "right",
        ...props
      },
      ref,
    ) => {
      const [noFocus, setNoFocus] = useState(false)
      const { labelId, messageId } = useLabelAndMessageId(props.id)
      const selectorRef = useRef<HTMLInputElement>(null)
      const { handleClick, handleBlur } = useFocusHandling(
        selectorRef,
        setNoFocus,
        onBlur,
      )
      useIndeterminate(selectorRef, props.checked, indeterminate)
      const handleChange = useHandleChange(onChange)

      useImperativeHandle(ref, () => selectorRef.current as HTMLInputElement)

      const selectorClassName = noFocus ? "no-focus" : ""

      return (
        <ControlContainer
          disabled={disabled}
          loading={loading}
          className={className}
          marginBottom={marginBottom}
          marginTop={marginTop}
          onClick={handleClick}
        >
          <InvisibleSelector
            {...props}
            aria-busy={loading}
            disabled={disabled}
            type="checkbox"
            onBlur={handleBlur}
            onChange={!loading ? handleChange : undefined}
            className={selectorClassName}
            ref={selectorRef}
            aria-invalid={error}
            aria-labelledby={labelId}
            aria-describedby={message && messageId}
          />
          <CheckboxStyles
            loading={loading}
            checked={props.checked}
            indeterminate={indeterminate}
          />
          <Label
            label={label}
            message={message}
            labelId={labelId}
            messageId={messageId}
            labelPosition={labelPosition}
            hideLabel={hideLabel}
          />
        </ControlContainer>
      )
    },
  ),
)

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a Switch as a visual aid. For a functional alternative, use
 * the normal Switch component.
 */
export const SwitchDecorative = memo<SelectorElementProps>(({ className, ...props }) => (
  <DecorativeContainer className={className}>
    <InvisibleSelector {...props} type="checkbox" readOnly aria-hidden tabIndex={-1} />
    <SwitchStyles />
  </DecorativeContainer>
))

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a RadioButton as a visual aid. For a functional alternative, use
 * the normal RadioButton component.
 */
export const RadioButtonDecorative = memo<RadioButtonDecorativeProps>(
  ({ className, ...props }) => (
    <DecorativeContainer className={className}>
      <InvisibleSelector {...props} type="radio" readOnly aria-hidden tabIndex={-1} />
      <RadioStyles />
    </DecorativeContainer>
  ),
)

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a Checkbox as a visual aid. For a functional alternative, use
 * the normal Checkbox component.
 */
export const CheckboxDecorative = memo<CheckboxDecorativeProps>(
  ({ className, indeterminate, ...props }) => {
    const selectorRef = useRef<HTMLInputElement>(null)
    useIndeterminate(selectorRef, props.checked, indeterminate)

    return (
      <DecorativeContainer className={className}>
        <InvisibleSelector
          {...props}
          ref={selectorRef}
          type="checkbox"
          readOnly
          aria-hidden
          tabIndex={-1}
        />
        <CheckboxStyles checked={props.checked} indeterminate={indeterminate} />
      </DecorativeContainer>
    )
  },
)
